import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import api from "../../../services/api";
import { EUserRole } from "../../../types/enums";
import { AuthContext } from "../../../contexts/AuthContext";
import { isAuthSupport } from "../../../utils/authRole";
import { useIntl } from "../../../contexts/IntlContext";
import { useFormHandler } from "../../../hooks/useFormHandler";
import { isValidEmail } from "../../../utils/isValidEmail";

import { EstablishmentAutocomplete } from "../../../components/ui/EstablishmentAutocomplete";
import { MenuItem, Typography } from "@mui/material";
import Checkbox from "../../../components/ui/Checkbox";
import Input from "../../../components/ui/Input";
import FormContainer from "../../../components/ui/FormContainer";
import { PageHeader } from "../../../components/PageHeader";
import { Breadcrumbs } from "../../../components/Breadcrumbs";

import { Establishment } from "../../../types/establishment";

interface UserFormData {
  name: string;
  email: string;
  role: EUserRole;
  active: boolean;
  selectedEstablishments: Establishment[];
}

export function SystemUserForm() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { authInfo } = useContext(AuthContext);
  // SUPPORT can only manage OWNER users (backend enforces the same rule).
  const supportRestricted = isAuthSupport(authInfo?.role);

  const {
    formData,
    updateField,
    handleSubmit,
    loading,
    isEdit,
    unsavedChangesBlocked,
    confirmDiscardChanges,
    cancelDiscardChanges,
    fieldErrors,
  } = useFormHandler<UserFormData>({
    baseEndpoint: "/app/users",
    redirectPath: "/usuarios",
    defaultData: {
      name: "",
      email: "",
      role: EUserRole.OWNER,
      active: true,
      selectedEstablishments: []
    },
    successMessageId: "systemUserForm.success",
    errorMessageId: "systemUserForm.error",
    validateFields: (data): { valid: boolean } => {
      const requiresEstablishments = data.role === EUserRole.OWNER;
      return {
        valid:
          !!data.name &&
          !!data.email &&
          !!data.role &&
          (!requiresEstablishments || formData.selectedEstablishments.length > 0),
      };
    },
    transformData: (data) => ({
      name: data.name,
      email: data.email,
      role: data.role,
      active: data.active,
      establishmentIds: data.selectedEstablishments.map(e => e.establishmentId)
    }),
    loadData: async (id: string) => {
      const response = await api.get(`/app/users/${id}`);
      const userData = response.data;

      return {
        name: userData.name,
        email: userData.email,
        role: userData.role,
        active: userData.active,
        selectedEstablishments: userData.establishments || []
      };
    }
  });

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const markTouched = (field: string) =>
    setTouched((prev) => ({ ...prev, [field]: true }));
  const requiredFieldError = (field: string, value: unknown, label: string) => {
    if (fieldErrors[field]) return fieldErrors[field];
    return touched[field] && !value
      ? intl.formatMessage({ id: "validation.required" }, { field: label })
      : "";
  };
  const emailError =
    fieldErrors.email ||
    (touched.email && formData.email && !isValidEmail(formData.email)
      ? intl.formatMessage({ id: "validation.invalidEmail" })
      : requiredFieldError("email", formData.email, intl.formatMessage({ id: "common.email" })));

  return (
    <>
      <PageHeader
        title={
          isEdit
            ? formData.name || intl.formatMessage({ id: "systemUser.title" })
            : intl.formatMessage({ id: "systemUserForm.create" })
        }
        breadcrumb={
          <Breadcrumbs
            items={[
              { label: intl.formatMessage({ id: "systemUser.title" }), onClick: () => navigate("/usuarios") },
              {
                label: isEdit
                  ? formData.name || intl.formatMessage({ id: "systemUser.title" })
                  : intl.formatMessage({ id: "systemUserForm.create" }),
              },
            ]}
            onBack={() => navigate("/usuarios")}
          />
        }
      />
      <FormContainer
        onSubmit={handleSubmit}
        loading={loading}
        submitText={isEdit ? 'common.update' : 'systemUserForm.create'}
        redirectPath="/usuarios"
        unsavedChangesBlocked={unsavedChangesBlocked}
        onConfirmDiscardChanges={confirmDiscardChanges}
        onCancelDiscardChanges={cancelDiscardChanges}
      >
      <Input
        label={intl.formatMessage({ id: 'common.name' })}
        type="text"
        value={formData.name}
        onChange={(e) => updateField('name', e.target.value)}
        onBlur={() => markTouched('name')}
        error={!!requiredFieldError("name", formData.name, intl.formatMessage({ id: "common.name" }))}
        helperText={requiredFieldError("name", formData.name, intl.formatMessage({ id: "common.name" }))}
        required
      />

      <Input
        label={intl.formatMessage({ id: 'common.email' })}
        type="email"
        value={formData.email}
        onChange={(e) => updateField('email', e.target.value)}
        onBlur={() => markTouched('email')}
        error={!!emailError}
        helperText={emailError}
        required
      />

      <Input
        label={intl.formatMessage({ id: 'systemUserForm.role' })}
        select
        value={formData.role}
        onChange={(e) => {
          const nextRole = e.target.value as EUserRole;
          if (
            formData.role === EUserRole.OWNER &&
            nextRole !== EUserRole.OWNER &&
            formData.selectedEstablishments.length > 0
          ) {
            toast.info(intl.formatMessage({ id: 'systemUserForm.establishmentsHiddenHint' }));
          }
          updateField('role', nextRole);
        }}
        helperText={intl.formatMessage({ id: 'systemUserForm.roleHelper' })}
      >
        {!supportRestricted && (
          <MenuItem value={EUserRole.ADMIN}>{intl.formatMessage({ id: 'appBar.role.ADMIN' })}</MenuItem>
        )}
        {!supportRestricted && (
          <MenuItem value={EUserRole.SUPPORT}>{intl.formatMessage({ id: 'appBar.role.SUPPORT' })}</MenuItem>
        )}
        <MenuItem value={EUserRole.OWNER}>{intl.formatMessage({ id: 'appBar.role.OWNER' })}</MenuItem>
      </Input>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, mt: 1 }}>
        {intl.formatMessage({ id: 'systemUserForm.inviteInfo' })}
      </Typography>

      <Checkbox
        label={intl.formatMessage({ id: 'common.active' })}
        checked={formData.active}
        onChange={(_event, checked) => updateField('active', checked)}
      />
      <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
        {intl.formatMessage({ id: 'systemUserForm.activeHelper' })}
      </Typography>

      {formData.role === EUserRole.OWNER && (
        <EstablishmentAutocomplete
          value={formData.selectedEstablishments}
          onChange={(establishments) => updateField('selectedEstablishments', establishments)}
          required={formData.selectedEstablishments.length === 0}
          sx={{ marginY: "0.4rem" }}
        />
      )}
      </FormContainer>
    </>
  );
}